"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/** Idle time before the drawn chrome retracts off a playing clip. */
export const CHROME_IDLE_MS = 3000;

export interface IdleChrome {
  /** Whether the overlays should currently be on screen. */
  visible: boolean;
  /** Bring them back and restart the countdown. */
  wake: () => void;
  /**
   * True when the click being handled is the one that brought the chrome back,
   * so the player can reveal on that tap instead of pausing on it.
   */
  pressRevealed: () => boolean;
}

/**
 * Retracts a player's overlays once a clip has been running untouched for a
 * while: the speaker card, gradients and transport bar together cover a good
 * third of the frame, which is fine over a poster and not fine over the film.
 * A press anywhere outside the frame retracts them at once, without waiting out
 * the timer: attention has visibly left the player.
 *
 * Only while `active`. A paused clip keeps its chrome, because the chrome is
 * where the play button lives and a frozen frame with nothing on it reads as
 * broken rather than clean.
 */
export function useIdleChrome(
  frameRef: RefObject<HTMLElement | null>,
  active: boolean,
): IdleChrome {
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<number | undefined>(undefined);

  // Mirrors of state for the native listeners, which are bound once and would
  // otherwise close over the first render's values.
  const activeRef = useRef(active);
  activeRef.current = active;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Whether the gesture in flight started with the chrome retracted. Recomputed
  // on every press, so it cannot go stale after a drag that never clicks.
  const pressRevealedRef = useRef(false);

  const wake = useCallback(() => {
    setVisible(true);
    window.clearTimeout(timerRef.current);
    if (!activeRef.current) {
      return;
    }
    timerRef.current = window.setTimeout(() => {
      setVisible(false);
    }, CHROME_IDLE_MS);
  }, []);

  const pressRevealed = useCallback(() => pressRevealedRef.current, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!active || !frame) {
      window.clearTimeout(timerRef.current);
      pressRevealedRef.current = false;
      setVisible(true);
      return;
    }

    wake();

    // A press has to record what it woke from before it wakes anything: React
    // re-renders off this listener, so by the time the click lands the chrome is
    // already back and the click handler can no longer tell.
    const onPress = () => {
      pressRevealedRef.current = !visibleRef.current;
      wake();
    };

    // Pointer movement anywhere over the frame counts, so the bar is already up
    // by the time a mouse reaches it. `focusin` covers the keyboard route in:
    // tabbing to a control that has faded out has to bring it back.
    const passive = ["pointermove", "keydown", "focusin"] as const;
    passive.forEach((type) => {
      frame.addEventListener(type, wake, { passive: true });
    });
    // `pointerdown` only, not also `touchstart`: a touch fires both, and the
    // second one recomputes the flag after the first has already woken the
    // chrome, which is how the reveal tap turned back into a pause.
    frame.addEventListener("pointerdown", onPress, { passive: true });

    // A press outside the frame retracts immediately. Bound on the document and
    // filtered by containment rather than on a wrapper, because the two players
    // sit in different trees (one in the page, one in a portal) and the useful
    // "outside" is everything that is not the frame.
    const onOutside = (event: Event) => {
      if (frame.contains(event.target as Node)) {
        return;
      }
      window.clearTimeout(timerRef.current);
      pressRevealedRef.current = false;
      setVisible(false);
    };
    document.addEventListener("pointerdown", onOutside, { passive: true });

    return () => {
      window.clearTimeout(timerRef.current);
      passive.forEach((type) => {
        frame.removeEventListener(type, wake);
      });
      frame.removeEventListener("pointerdown", onPress);
      document.removeEventListener("pointerdown", onOutside);
    };
  }, [active, frameRef, wake]);

  return { visible, wake, pressRevealed };
}
